import { addDays, weekdayOf } from './businessDate';
import { SECTION_KIND, type BusinessDate, type CountEntry, type Item, type Kind, type Movement, type Night, type SectionId } from './types';

/**
 * The sheet's arithmetic, one function per row, written to give exactly what Red's Excel
 * formulas give (an empty cell counts as 0, as it does in Excel). The weekly export writes
 * those same formulas, and a test checks the two agree.
 *
 * Beer Count Close (one column per night)
 *   TOTAL BEER          = every count in both beer lists
 *   Opening Beer        = Monday: typed in · other days: the night before's CLOSING Beer
 *   TOTAL Opening       = Opening + Beer Added − Transfered Out − Promo/Spillage
 *   CLOSING Beer        = TOTAL BEER
 *   TOTAL BEER SOLD     = TOTAL Opening − CLOSING Beer
 *   MICROS BEER SOLD    = 1 BTL + 2 × 2 BTL + 5 × 5 BTL + 1 BTL Cooler + 1 STAFF BEER
 *   Variance (+/−)      = MICROS BEER SOLD − TOTAL BEER SOLD
 *
 * Closing Liquor
 *   Gun opening         = Monday: typed in · other days: the night before's closing reading
 *   Gun Total Used      = Closing − Opening (for each of the two guns)
 *   Opening Inventory   = Monday: typed in · other days: the night before's CLOSING Inventory
 *   TOTAL opening       = Opening Inventory + Liquor Added − Promo/Spillage
 *   CLOSING Inventory   = TOTAL OZ (liquor list) + TOTAL OZ (liqueur list)
 *   Back Bar Used       = TOTAL opening − CLOSING Inventory
 *   TOTAL OZ SOLD       = Gun 1 used + Gun 2 used + Back Bar Used
 *   MICROS OZ SOLD      = 1OZ + 2 × 2OZ + 3 × 3OZ + 4 × 4OZ + STAFF LIQUOR
 *   Variance (+/−)      = MICROS OZ SOLD − TOTAL OZ SOLD
 *
 * Opening numbers typed by a manager (Night.openings) replace the "night before" value.
 * A positive variance means Micros rang up more than the counts show went out; negative
 * means more went out than was rung up. Either way it only says "requires review".
 */

export interface SheetData {
  items: Item[];
  nights: Map<BusinessDate, Night>;
  /** Count entries by date. */
  counts: Map<BusinessDate, CountEntry[]>;
  /** Movements by date (voided ones already left out). */
  moves: Map<BusinessDate, Movement[]>;
}

const n0 = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? 0 : v);

export function isMonday(date: BusinessDate): boolean {
  return weekdayOf(date) === 1;
}

/** The Monday of the week a date is in (weeks run Monday to Sunday, like the sheets). */
export function weekStartOf(date: BusinessDate): BusinessDate {
  return addDays(date, -((weekdayOf(date) + 6) % 7));
}

export function weekDates(monday: BusinessDate): BusinessDate[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** Total of one list's counts on a night (empty cells = 0, as in Excel). */
export function sectionTotal(data: SheetData, date: BusinessDate, section: SectionId): number {
  return (data.counts.get(date) ?? []).reduce((s, c) => (c.section === section ? s + n0(c.value) : s), 0);
}

/** True when anything was counted for that sheet on the night. */
export function hasCounts(data: SheetData, date: BusinessDate, kind: Kind): boolean {
  return (data.counts.get(date) ?? []).some((c) => SECTION_KIND[c.section] === kind && c.value != null);
}

export function moveTotal(data: SheetData, date: BusinessDate, sheet: Kind, kind: Movement['kind']): number {
  return (data.moves.get(date) ?? []).reduce((s, m) => (m.sheet === sheet && m.kind === kind ? s + m.qty : s), 0);
}

export function hasMoves(data: SheetData, date: BusinessDate, sheet: Kind, kind: Movement['kind']): boolean {
  return (data.moves.get(date) ?? []).some((m) => m.sheet === sheet && m.kind === kind);
}

export interface BeerDay {
  totalBeer: number;
  /** Where the opening number came from. */
  openingFrom: 'typed' | 'night_before' | 'missing';
  opening: number;
  added: number;
  transferOut: number;
  promoSpill: number;
  totalOpening: number;
  closing: number;
  sold: number;
  micros: number;
  variance: number;
}

export interface LiquorDay {
  liquorTotal: number;
  liqueurTotal: number;
  gun1Opening: number;
  gun1OpeningFrom: 'typed' | 'night_before' | 'missing';
  gun1Closing: number | null;
  gun1Used: number;
  gun2Opening: number;
  gun2OpeningFrom: 'typed' | 'night_before' | 'missing';
  gun2Closing: number | null;
  gun2Used: number;
  openingFrom: 'typed' | 'night_before' | 'missing';
  opening: number;
  added: number;
  promoSpill: number;
  totalOpening: number;
  closing: number;
  backBarUsed: number;
  totalSold: number;
  micros: number;
  variance: number;
}

export function beerClosing(data: SheetData, date: BusinessDate): number {
  return sectionTotal(data, date, 'back_beer') + sectionTotal(data, date, 'front_beer');
}

export function liquorClosing(data: SheetData, date: BusinessDate): number {
  return sectionTotal(data, date, 'liquor') + sectionTotal(data, date, 'liqueur');
}

/**
 * Where an opening number comes from: a typed opening wins; otherwise the night before.
 * With nothing from the night before it is 0 — what the sheet's formula gives for an empty
 * column — and marked "missing" so the screen can say so. On Mondays the sheet's cell is
 * typed rather than a formula, so the export writes this value there.
 */
function opening(typed: number | null, beforeHas: boolean, beforeValue: () => number): { value: number; from: 'typed' | 'night_before' | 'missing' } {
  if (typed != null) return { value: typed, from: 'typed' };
  if (beforeHas) return { value: beforeValue(), from: 'night_before' };
  return { value: 0, from: 'missing' };
}

export function beerDay(data: SheetData, date: BusinessDate): BeerDay {
  const night = data.nights.get(date);
  const before = addDays(date, -1);
  const open = opening(night?.openings.beer ?? null, hasCounts(data, before, 'beer'), () => beerClosing(data, before));
  const added = moveTotal(data, date, 'beer', 'added');
  const transferOut = moveTotal(data, date, 'beer', 'transfer_out');
  const promoSpill = moveTotal(data, date, 'beer', 'promo_spill');
  const totalBeer = beerClosing(data, date);
  const totalOpening = open.value + added - transferOut - promoSpill;
  const sold = totalOpening - totalBeer;
  const m = night?.micros;
  const micros = n0(m?.btl1) + n0(m?.btl2) * 2 + n0(m?.btl5) * 5 + n0(m?.btlCooler) + n0(m?.staffBeer);
  return { totalBeer, openingFrom: open.from, opening: open.value, added, transferOut, promoSpill, totalOpening, closing: totalBeer, sold, micros, variance: micros - sold };
}

export function liquorDay(data: SheetData, date: BusinessDate): LiquorDay {
  const night = data.nights.get(date);
  const before = addDays(date, -1);
  const prevNight = data.nights.get(before);
  const g1 = opening(night?.openings.gun1 ?? null, prevNight?.gun1 != null, () => prevNight!.gun1!);
  const g2 = opening(night?.openings.gun2 ?? null, prevNight?.gun2 != null, () => prevNight!.gun2!);
  const open = opening(night?.openings.liquor ?? null, hasCounts(data, before, 'liquor'), () => liquorClosing(data, before));
  const liquorTotal = sectionTotal(data, date, 'liquor');
  const liqueurTotal = sectionTotal(data, date, 'liqueur');
  const added = moveTotal(data, date, 'liquor', 'added');
  const promoSpill = moveTotal(data, date, 'liquor', 'promo_spill');
  const totalOpening = open.value + added - promoSpill;
  const closing = liquorTotal + liqueurTotal;
  const backBarUsed = totalOpening - closing;
  const gun1Closing = night?.gun1 ?? null;
  const gun2Closing = night?.gun2 ?? null;
  const gun1Used = n0(gun1Closing) - g1.value;
  const gun2Used = n0(gun2Closing) - g2.value;
  const totalSold = gun1Used + gun2Used + backBarUsed;
  const m = night?.micros;
  const micros = n0(m?.oz1) + n0(m?.oz2) * 2 + n0(m?.oz3) * 3 + n0(m?.oz4) * 4 + n0(m?.staffLiquor);
  return {
    liquorTotal,
    liqueurTotal,
    gun1Opening: g1.value,
    gun1OpeningFrom: g1.from,
    gun1Closing,
    gun1Used,
    gun2Opening: g2.value,
    gun2OpeningFrom: g2.from,
    gun2Closing,
    gun2Used,
    openingFrom: open.from,
    opening: open.value,
    added,
    promoSpill,
    totalOpening,
    closing,
    backBarUsed,
    totalSold,
    micros,
    variance: micros - totalSold,
  };
}

/** Whether the Micros numbers for a sheet were typed in at all (blank ≠ 0). */
export function microsEntered(night: Night | undefined, kind: Kind): boolean {
  if (!night) return false;
  const m = night.micros;
  const fields = kind === 'beer' ? [m.btl1, m.btl2, m.btl5, m.btlCooler, m.staffBeer] : [m.oz1, m.oz2, m.oz3, m.oz4, m.staffLiquor];
  return fields.some((v) => v != null);
}

// ---------------------------------------------------------------------------
// Per item: what each drink did between two counts
// ---------------------------------------------------------------------------

/** The lists an item is on (beer: back and/or front; liquor: one). */
export function itemSections(item: Item): SectionId[] {
  return (Object.keys(item.rows) as SectionId[]).filter((s) => item.rows[s] != null);
}

/**
 * An item's count on a night across all its lists, or null when any list it's on wasn't
 * counted (a missing count is never treated as zero here).
 *
 * A list the drink wasn't on yet that night doesn't count against it: when that night's area
 * was finished (an area can't be finished with a listed drink left blank) and the drink has
 * no entry there, it wasn't on that list then — e.g. a beer added to the front cooler list
 * later keeps its earlier back-beer history.
 */
export function itemClose(data: SheetData, item: Item, date: BusinessDate): number | null {
  const entries = (data.counts.get(date) ?? []).filter((c) => c.itemId === item.id);
  const night = data.nights.get(date);
  const sections = new Set([...itemSections(item), ...entries.map((e) => e.section)]);
  let total = 0;
  let counted = 0;
  for (const s of sections) {
    const e = entries.find((x) => x.section === s);
    if (!e && night && (night.status !== 'counting' || night.areasDone[s === 'liqueur' ? 'liquor' : s])) continue;
    if (!e || e.value == null) return null;
    total += e.value;
    counted++;
  }
  return counted ? total : null;
}

export interface ItemMoves {
  added: number;
  out: number;
}

export function itemMoves(data: SheetData, itemId: string, date: BusinessDate): ItemMoves {
  let added = 0;
  let out = 0;
  for (const m of data.moves.get(date) ?? []) {
    if (m.itemId !== itemId) continue;
    if (m.kind === 'added') added += m.qty;
    else out += m.qty;
  }
  return { added, out };
}

export interface ItemNight {
  /** Last night with a full count of this item before this one, within `lookbackDays`. */
  prevDate: BusinessDate | null;
  prev: number | null;
  /** Came in / went out (transfers, promo, spills) since the previous count. */
  added: number;
  out: number;
  close: number | null;
  /** prev + added − out − close. Null when either count is missing. Negative = it went up. */
  used: number | null;
  /** Days between the two counts (1 = the night before). */
  days: number;
}

export function itemNight(data: SheetData, item: Item, date: BusinessDate, lookbackDays = 7): ItemNight {
  const close = itemClose(data, item, date);
  let prevDate: BusinessDate | null = null;
  let prev: number | null = null;
  for (let i = 1; i <= lookbackDays; i++) {
    const d = addDays(date, -i);
    const v = itemClose(data, item, d);
    if (v != null) {
      prevDate = d;
      prev = v;
      break;
    }
  }
  let added = 0;
  let out = 0;
  const days = prevDate ? Math.round((Date.parse(date) - Date.parse(prevDate)) / 86_400_000) : 1;
  for (let i = 0; i < days; i++) {
    const m = itemMoves(data, item.id, addDays(date, -i));
    added += m.added;
    out += m.out;
  }
  const used = close != null && prev != null ? prev + added - out - close : null;
  return { prevDate, prev, added, out, close, used, days };
}
